import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Settings, Wallet, Lock, Pencil, X } from 'lucide-react';
import { getMyP2PProfile, getUserP2PProfile, followUser, blockUser, getMyAds, getMyOrders, updateMyP2PProfile } from '../../services/p2pService';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { authFetch, getAuthHeaders } from '../../lib/authFetch';

type ProfileTab = 'trade' | 'notifications' | 'others';

export default function P2PProfile() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { toast } = useToast();
    const isOwn = !userId || userId === user?._id;

    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [following, setFollowing] = useState(false);
    const [tab, setTab] = useState<ProfileTab>('trade');
    const [balance, setBalance] = useState<number>(0);
    const [lockedInAds, setLockedInAds] = useState<number>(0);
    const [lockedInOrders, setLockedInOrders] = useState<number>(0);
    const [lockedInSignals, setLockedInSignals] = useState<number>(0);

    // Nickname edit
    const [showNicknameModal, setShowNicknameModal] = useState(false);
    const [nicknameInput,     setNicknameInput]     = useState('');
    const [nicknameSaving,    setNicknameSaving]     = useState(false);
    const [nicknameError,     setNicknameError]      = useState('');

    useEffect(() => {
        const fn = isOwn ? getMyP2PProfile : () => getUserP2PProfile(userId!);
        fn().then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));

        if (isOwn) {
            // Fetch live balance
            authFetch(`${import.meta.env.VITE_API_BASE_URL}/api/users/profile`, { headers: getAuthHeaders() })
                .then(r => r.json())
                .then(d => setBalance(d?.balance ?? 0))
                .catch(() => {});

            // Locked in active sell ads
            getMyAds().then(ads => {
                const locked = ads
                    .filter((a: any) => ['active', 'paused'].includes(a.status) && a.side === 'sell')
                    .reduce((sum: number, a: any) => sum + ((a.totalAmount ?? 0) - (a.filledAmount ?? 0)), 0);
                setLockedInAds(locked);
            }).catch(() => {});

            // Locked in active orders as seller (escrow)
            getMyOrders({ status: 'pending_payment' }).then(res => {
                const orders = res.orders ?? [];
                const locked = orders
                    .filter((o: any) => o.seller?._id === user?._id || o.seller === user?._id)
                    .reduce((sum: number, o: any) => sum + (o.escrowAmount ?? o.cryptoAmount ?? 0), 0);
                setLockedInOrders(locked);
            }).catch(() => {});

            // Locked in active signal follows
            authFetch(`${import.meta.env.VITE_API_BASE_URL}/api/signals?status=active`, { headers: getAuthHeaders() })
                .then(r => r.json())
                .then(d => {
                    const signals = d?.data?.signals ?? d?.data ?? [];
                    // Get my position for each active signal
                    let locked = 0;
                    // We don't have the full follower data client-side, but we can estimate
                    // by checking which signals the user follows via position endpoint
                    setLockedInSignals(locked);
                }).catch(() => {});
        }
    }, [userId]);

    if (loading) return (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="p2p-skel" style={{ width: 52, height: 52, borderRadius: '50%' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span className="p2p-skel" style={{ width: 130, height: 16 }} />
                    <span className="p2p-skel" style={{ width: 80, height: 12 }} />
                </div>
            </div>
            <span className="p2p-skel" style={{ width: '100%', height: 12 }} />
            <span className="p2p-skel" style={{ width: '70%', height: 12 }} />
            <span className="p2p-skel" style={{ width: '100%', height: 60, borderRadius: 8 }} />
            <span className="p2p-skel" style={{ width: '100%', height: 60, borderRadius: 8 }} />
        </div>
    );
    if (!data) return <div className="p2p-empty"><p className="p2p-empty-text">Profile not found</p></div>;

    const profileUser = data.user ?? {};
    const profile = data.profile ?? {};

    const handleFollow = async () => {
        if (!userId) return;
        try {
            const res = await followUser(userId);
            setFollowing(res.following);
            toast(res.following ? 'Following' : 'Unfollowed', 'success');
        } catch (e: any) { toast(e.message, 'error'); }
    };

    const handleBlock = async () => {
        if (!userId) return;
        try {
            await blockUser(userId);
            toast('User blocked', 'success');
            navigate(-1);
        } catch (e: any) { toast(e.message, 'error'); }
    };

    const handleSaveNickname = async () => {
        const trimmed = nicknameInput.trim();
        if (trimmed.length < 2 || trimmed.length > 20) {
            setNicknameError('Must be 2–20 characters'); return;
        }
        if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
            setNicknameError('Letters, numbers, underscores only'); return;
        }
        setNicknameError('');
        setNicknameSaving(true);
        try {
            const updated = await updateMyP2PProfile({ nickname: trimmed });
            setData((prev: any) => ({ ...prev, profile: { ...prev.profile, nickname: trimmed } }));
            setShowNicknameModal(false);
            toast('Nickname updated', 'success');
        } catch (e: any) {
            setNicknameError(e.message || 'Failed to save nickname');
        } finally {
            setNicknameSaving(false);
        }
    };

    return (
        <>
        <div style={{ background: 'var(--p2p-bg-primary)', minHeight: '100vh' }}>
            <div className="p2p-header">
                <button className="p2p-back-btn" onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
                <span className="p2p-header-title">{isOwn ? 'My Profile' : ''}</span>
                {isOwn && (
                    <button style={{ background: 'none', border: 'none', color: 'var(--p2p-text-muted)', cursor: 'pointer' }} onClick={() => navigate('/p2p/payment-methods')}>
                        <Settings size={20} />
                    </button>
                )}
            </div>

            <div style={{ padding: '20px 16px' }}>
                {/* User Info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                    <div style={{ position: 'relative' }}>
                        {profileUser.avatar
                            ? <img src={profileUser.avatar} alt={profileUser.name} className="p2p-avatar-lg" style={{ objectFit: 'cover' }} />
                            : <div className="p2p-avatar-lg">{(profileUser.name ?? '?').slice(0, 1).toUpperCase()}</div>
                        }
                        <span style={{ position: 'absolute', bottom: 2, right: 2, width: 12, height: 12, borderRadius: '50%', background: profile.isOnline ? 'var(--p2p-buy)' : 'var(--p2p-text-disabled)', border: '2px solid var(--p2p-bg-primary)' }} />
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--p2p-text-primary)' }}>
                                {profile.nickname || profileUser.name}
                            </span>
                            {profileUser.kycStatus === 'approved' && (
                                <div className="p2p-verified-badge"><Check size={10} color="#000" /></div>
                            )}
                            {profileUser.isMerchant && (
                                <span className="p2p-badge" style={{ background: 'rgba(240,185,11,0.15)', color: '#F0B90B' }}>Merchant</span>
                            )}
                            {isOwn && (
                                <button
                                    onClick={() => { setNicknameInput(profile.nickname || ''); setNicknameError(''); setShowNicknameModal(true); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--p2p-text-muted)', padding: 2, display: 'flex' }}
                                >
                                    <Pencil size={14} />
                                </button>
                            )}
                        </div>
                        {isOwn && profile.nickname && (
                            <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)', marginTop: 2 }}>
                                Real name: {profileUser.name}
                            </div>
                        )}
                        <div style={{ fontSize: 13, color: 'var(--p2p-text-muted)', marginTop: 4 }}>
                            {profile.isOnline ? '● Online' : `Last seen ${profile.lastSeen ? new Date(profile.lastSeen).toLocaleDateString() : 'recently'}`}
                        </div>
                    </div>
                </div>

                {/* Verification Badges */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                    {profileUser.isEmailVerified && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--p2p-buy)' }}>
                            <Check size={14} />Email
                        </span>
                    )}
                    {profileUser.phone && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--p2p-buy)' }}>
                            <Check size={14} />SMS
                        </span>
                    )}
                    {profileUser.kycStatus === 'verified' && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--p2p-buy)' }}>
                            <Check size={14} />KYC
                        </span>
                    )}
                </div>

                {/* Action buttons for other user */}
                {!isOwn && (
                    <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                        <button
                            className="p2p-btn-secondary"
                            style={{ flex: 1, borderColor: following ? 'var(--p2p-buy)' : 'var(--p2p-border-input)', color: following ? 'var(--p2p-buy)' : 'var(--p2p-text-primary)' }}
                            onClick={handleFollow}
                        >
                            {following ? 'Following' : 'Follow'}
                        </button>
                        <button className="p2p-btn-danger" style={{ flex: 1, fontSize: 14 }} onClick={handleBlock}>Block</button>
                    </div>
                )}

                {/* Tabs: Trade / Notifications / Others */}
                <div className="p2p-tabs" style={{ marginBottom: 20 }}>
                    <button className={`p2p-tab ${tab === 'trade' ? 'active' : ''}`} onClick={() => setTab('trade')}>Trade</button>
                    <button className={`p2p-tab ${tab === 'notifications' ? 'active' : ''}`} onClick={() => setTab('notifications')}>Notifications</button>
                    <button className={`p2p-tab ${tab === 'others' ? 'active' : ''}`} onClick={() => setTab('others')}>Others</button>
                </div>

                {/* Tab: Trade */}
                {tab === 'trade' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* Stats Grid */}
                        <div className="p2p-stats-grid">
                            <div className="p2p-stat">
                                <div className="p2p-stat-value">{profile.completedTrades30d ?? 0}</div>
                                <div className="p2p-stat-label">30d Trades</div>
                            </div>
                            <div className="p2p-stat">
                                <div className="p2p-stat-value">{profile.completionRate30d ?? 100}%</div>
                                <div className="p2p-stat-label">30d Completion Rate</div>
                            </div>
                            <div className="p2p-stat">
                                <div className="p2p-stat-value">{profile.avgReleaseTimeMinutes ?? 0} min</div>
                                <div className="p2p-stat-label">Avg Release Time</div>
                            </div>
                            <div className="p2p-stat">
                                <div className="p2p-stat-value">{profile.avgPayTimeMinutes ?? 0} min</div>
                                <div className="p2p-stat-label">Avg Pay Time</div>
                            </div>
                        </div>

                        {/* Ratings */}
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1, textAlign: 'center', padding: 12, background: 'var(--p2p-buy-subtle)', borderRadius: 8 }}>
                                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--p2p-buy)' }}>👍 {profile.positiveRatings ?? 0}</div>
                                <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>Positive</div>
                            </div>
                            <div style={{ flex: 1, textAlign: 'center', padding: 12, background: 'var(--p2p-sell-subtle)', borderRadius: 8 }}>
                                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--p2p-sell)' }}>👎 {profile.negativeRatings ?? 0}</div>
                                <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>Negative</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Notifications */}
                {tab === 'notifications' && (
                    <div className="p2p-card">
                        <p style={{ fontSize: 14, color: 'var(--p2p-text-muted)', textAlign: 'center', padding: '20px 0' }}>
                            No notifications yet
                        </p>
                    </div>
                )}

                {/* Tab: Others */}
                {tab === 'others' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {/* Balance summary — own profile only */}
                        {isOwn && (
                            <div className="p2p-card" style={{ marginBottom: 4 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--p2p-text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Wallet size={14} color="var(--p2p-buy)" /> Balance Overview
                                </div>
                                <div className="p2p-row">
                                    <span className="p2p-row-label">Available Balance</span>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--p2p-buy)' }}>{parseFloat(balance.toFixed(4)).toString()} USDT</span>
                                </div>
                                {lockedInAds > 0 && (
                                    <div className="p2p-row">
                                        <span className="p2p-row-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Lock size={11} />Locked in Ads</span>
                                        <span style={{ fontSize: 14, color: 'var(--p2p-warning)' }}>{parseFloat(lockedInAds.toFixed(4)).toString()} USDT</span>
                                    </div>
                                )}
                                {lockedInOrders > 0 && (
                                    <div className="p2p-row">
                                        <span className="p2p-row-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Lock size={11} />Locked in Orders</span>
                                        <span style={{ fontSize: 14, color: 'var(--p2p-warning)' }}>{parseFloat(lockedInOrders.toFixed(4)).toString()} USDT</span>
                                    </div>
                                )}
                                {(lockedInAds > 0 || lockedInOrders > 0) && (
                                    <div className="p2p-row" style={{ border: 'none' }}>
                                        <span className="p2p-row-label">Total P2P Balance</span>
                                        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--p2p-text-primary)' }}>
                                            {parseFloat((balance + lockedInAds + lockedInOrders).toFixed(4)).toString()} USDT
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                        {isOwn && (
                            <>
                                <button className="p2p-btn-secondary" onClick={() => navigate('/p2p/payment-methods')}>
                                    Manage Payment Methods
                                </button>
                                <button className="p2p-btn-secondary" onClick={() => navigate('/p2p/post-ad/1')}>
                                    Post a New Ad
                                </button>
                            </>
                        )}
                        <div className="p2p-row">
                            <span className="p2p-row-label">Member since</span>
                            <span className="p2p-row-value">{profileUser.createdAt ? new Date(profileUser.createdAt).toLocaleDateString() : '—'}</span>
                        </div>
                        <div className="p2p-row">
                            <span className="p2p-row-label">Total trades</span>
                            <span className="p2p-row-value">{profile.totalTrades ?? 0}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* Nickname edit modal */}
        {showNicknameModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
                onClick={() => setShowNicknameModal(false)}>
                <div onClick={e => e.stopPropagation()}
                    style={{ background: '#ffffff', borderRadius: '16px 16px 0 0', padding: '24px 20px', width: '100%', maxWidth: 480, paddingBottom: 'calc(24px + env(safe-area-inset-bottom))' }}>
                    <div style={{ width: 40, height: 4, background: '#e4e4e7', borderRadius: 2, margin: '0 auto 20px' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <span style={{ fontSize: 17, fontWeight: 700, color: '#000' }}>Set P2P Nickname</span>
                        <button onClick={() => setShowNicknameModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#71717a' }}><X size={20} /></button>
                    </div>
                    <p style={{ fontSize: 13, color: '#71717a', marginBottom: 16, lineHeight: 1.5 }}>
                        Your nickname is shown to other P2P users instead of your real name. It must be unique across the platform.
                    </p>
                    <div style={{ marginBottom: 8 }}>
                        <input
                            type="text"
                            value={nicknameInput}
                            onChange={e => { setNicknameInput(e.target.value); setNicknameError(''); }}
                            placeholder="e.g. CryptoTrader_KE"
                            maxLength={20}
                            style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${nicknameError ? '#ef4444' : '#e4e4e7'}`, borderRadius: 10, padding: '12px 14px', fontSize: 15, fontWeight: 600, color: '#000', outline: 'none', background: '#f9f9f9' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                            {nicknameError
                                ? <span style={{ fontSize: 12, color: '#ef4444' }}>{nicknameError}</span>
                                : <span style={{ fontSize: 12, color: '#71717a' }}>Letters, numbers, underscores only</span>
                            }
                            <span style={{ fontSize: 12, color: nicknameInput.length > 18 ? '#ef4444' : '#71717a' }}>{nicknameInput.length}/20</span>
                        </div>
                    </div>
                    <div style={{ background: '#f4f4f5', borderRadius: 8, padding: '10px 12px', marginBottom: 20 }}>
                        {([
                            ['2–20 characters', nicknameInput.length >= 2 && nicknameInput.length <= 20],
                            ['Letters, numbers, underscores only', /^[a-zA-Z0-9_]*$/.test(nicknameInput)],
                            ['No spaces', !nicknameInput.includes(' ')],
                        ] as [string, boolean][]).map(([label, valid]) => (
                            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <div style={{ width: 16, height: 16, borderRadius: '50%', background: (nicknameInput.length > 0 && valid) ? '#10b981' : '#d4d4d8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Check size={10} color="#fff" />
                                </div>
                                <span style={{ fontSize: 12, color: '#52525b' }}>{label}</span>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={handleSaveNickname}
                        disabled={nicknameSaving || nicknameInput.trim().length < 2}
                        style={{ width: '100%', background: nicknameSaving || nicknameInput.trim().length < 2 ? '#a1a1aa' : '#F0B90B', border: 'none', borderRadius: 10, padding: '14px 0', color: '#000', fontWeight: 700, fontSize: 16, cursor: nicknameSaving || nicknameInput.trim().length < 2 ? 'not-allowed' : 'pointer' }}
                    >
                        {nicknameSaving ? 'Saving…' : 'Save Nickname'}
                    </button>
                </div>
            </div>
        )}
    </>
    );
}
